'use client';

import {
  type CSSProperties,
  Fragment,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { WaMessageBubble } from '../whatsapp-message-bubble';
import { ReadCheck } from '../whatsapp-read-check';
import {
  WA_EXPLAINER_TIMINGS,
  type WaExplainerPhase,
  type WaExplainerTimings,
  type WaStep,
  runRevealEngine,
  runStepsEngine,
} from './whatsapp-engine';

export type { WaStep, WaExplainerPhase, WaExplainerTimings };
export { WA_EXPLAINER_TIMINGS };

/** Read-checkmark (double tick) – Definition liegt im abhängigkeitsfreien
 *  Blatt-Modul `_components/whatsapp-read-check.tsx` (kein Import-Zyklus,
 *  Server-Pfad für die Server-Renderer). Der Re-Export hält die
 *  Design-Sync-Preview `previews/ReadCheck.tsx` (`@locari/website`) und alle
 *  Bestandsimporte unverändert lauffähig. */
export { ReadCheck };

/* ------------------------------------------------------------------ *
 * Device-chrome SVGs – recreated 1:1 from the design handoff
 * (`docs/design/design_handoff_onboarding_flow/design-reference/
 * Onboarding.dc.html`, the phone block). These are legitimate
 * third-party (iOS / WhatsApp) brand mocks, not app tokens – the exact
 * values are intentional; do not "simplify" them.
 * ------------------------------------------------------------------ */

/** Silenced-ringer bell shown left in the status bar (next to the live time). */
const STATUSBAR_BELL_SVG = (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="#000"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    <path d="m2 2 20 20" />
  </svg>
);

/** Signal / wifi / battery cluster (right side of the status bar). The battery
 * percentage sits INSIDE the filled body (white, bold – see design). */
const STATUSBAR_ICONS = (
  <span className="whatsapp-statusbar-icons">
    <svg
      viewBox="0 0 18 12"
      width="23"
      height="15"
      fill="#000"
      aria-hidden="true"
    >
      <rect x="0" y="8" width="3" height="4" rx=".5" />
      <rect x="5" y="5" width="3" height="7" rx=".5" />
      <rect x="10" y="2.5" width="3" height="9.5" rx=".5" />
      <rect x="15" y="0" width="3" height="12" rx=".5" opacity=".35" />
    </svg>
    <svg
      viewBox="0 0 16 12"
      width="22"
      height="15"
      fill="none"
      stroke="#000"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M2 5c1.9-1.8 4.2-2.8 6-2.8s4.1 1 6 2.8" />
      <path d="M4 7.2c1.2-1.1 2.6-1.7 4-1.7s2.8.6 4 1.7" />
      <circle cx="8" cy="9.6" r="1" fill="#000" stroke="none" />
    </svg>
    <span className="whatsapp-battery" aria-hidden="true">
      <svg viewBox="0 0 26 13" width="40" height="19" fill="none">
        <rect
          x=".5"
          y=".5"
          width="22"
          height="12"
          rx="3"
          stroke="#000"
          strokeWidth="1"
          opacity=".4"
        />
        <rect x="2" y="2" width="19" height="9" rx="1.6" fill="#000" />
        <rect
          x="23.6"
          y="4.5"
          width="2"
          height="4"
          rx=".8"
          fill="#000"
          opacity=".4"
        />
      </svg>
      <span className="whatsapp-battery-pct">90</span>
    </span>
  </span>
);

/** Round liquid-glass back-button chevron. Rendered larger than the "1" unread count. */
const HEADER_BACK_SVG = (
  <svg
    viewBox="0 0 24 24"
    width="21"
    height="21"
    fill="none"
    stroke="#0a0a0a"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

/** Locari "O" logomark avatar – navy circle + white ring mark. The path (and its
 * cut-out mask) is taken verbatim from the design file so it matches exactly. */
const AVATAR_SVG = (
  <svg viewBox="0 0 32 32" width="38" height="38" aria-hidden="true">
    <defs>
      <mask id="waAvCut">
        <rect width="32" height="32" fill="#fff" />
        <path d="M 12.2 14.3 L 5.8 7.9 L 8.5 5.1 L 15.0 11.6 Z" fill="#000" />
      </mask>
    </defs>
    <circle cx="16" cy="16" r="16" fill="#202a68" />
    <path
      d="M16.01 26.46Q13.33 26.46 11.21 25.21Q9.10 23.97 7.87 21.63Q6.65 19.29 6.65 16.01L6.65 16.01Q6.65 12.72 7.87 10.37Q9.10 8.03 11.21 6.79Q13.33 5.54 16.01 5.54L16.01 5.54Q18.68 5.54 20.79 6.79Q22.90 8.03 24.13 10.37Q25.35 12.72 25.35 16.01L25.35 16.01Q25.35 19.29 24.13 21.64Q22.90 23.98 20.79 25.22Q18.68 26.46 16.01 26.46ZM16.01 23.19Q17.67 23.19 18.95 22.36Q20.22 21.54 20.95 19.93Q21.67 18.32 21.67 16.01L21.67 16.01Q21.67 13.68 20.95 12.07Q20.22 10.46 18.95 9.64Q17.67 8.81 16.01 8.81L16.01 8.81Q14.35 8.81 13.06 9.64Q11.78 10.48 11.04 12.08Q10.31 13.69 10.31 16.01L10.31 16.01Q10.31 18.32 11.04 19.92Q11.78 21.52 13.06 22.36Q14.35 23.19 16.01 23.19Z"
      fill="#fff"
      mask="url(#waAvCut)"
    />
  </svg>
);

/** Send paper-plane (composing state). */
const SEND_SVG = (
  <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
    <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10.2 15 12 3.4 13.8Z" fill="#fff" />
  </svg>
);

/** "+" attach glyph (left of the input field). */
const INPUT_PLUS_SVG = (
  <svg
    viewBox="0 0 24 24"
    width="26"
    height="26"
    fill="none"
    stroke="#54656f"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

/** Sticker glyph inside the input field. */
const INPUT_STICKER_SVG = (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="#54656f"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 3H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h6l9-9V6a3 3 0 0 0-3-3z" />
    <path d="M13 21v-5a2 2 0 0 1 2-2h5" />
  </svg>
);

/** Camera + microphone cluster shown when the input is idle (not composing). */
const INPUT_CAM_MIC_SVGS = (
  <span className="whatsapp-input-cammic" aria-hidden="true">
    <svg
      viewBox="0 0 24 24"
      width="23"
      height="23"
      fill="none"
      stroke="#54656f"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="#54656f"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 17v5" />
    </svg>
  </span>
);

/* ------------------------------------------------------------------ *
 * reveal-mode (former WhatsappChat) – data + helpers.
 * ------------------------------------------------------------------ */

interface RevealBubble {
  text: string;
  time: string;
  status?: string;
}

const STATUS_CHECK_SVG = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** Wandelt `\n` im Bubble-Text in `<br/>` um (analog `textHtml` im OD-Script). */
function renderText(text: string) {
  return text.split('\n').map((line, i) => (
    <Fragment key={i}>
      {i > 0 ? <br /> : null}
      {line}
    </Fragment>
  ));
}

/** Live wall-clock in `H:MM` (24h, no leading hour zero) – matches the design. */
function computeLiveTime(): string {
  const now = new Date();
  return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
}

interface WhatsappPhoneProps {
  /** Animations-Modus.
   *  - `'steps'` (Default): heutiges WhatsappPhone-Verhalten (Loop/runOnce).
   *  - `'reveal'`: datengetriebene Bubble-Enthüllung (früher `WhatsappChat`).
   *  - `'explainer'`: Onboarding-Connect-Explainer (compose→sent→typing→reply
   *    →Loop). Copy UND Timings sind Caller-Props (dumb component). */
  mode?: 'steps' | 'reveal' | 'explainer';

  /* steps-mode (A / B) – API identisch zur früheren WhatsappPhone. */
  steps?: WaStep[];
  messages?: ReactNode;
  autoStart?: boolean;
  runOnce?: boolean;
  onComplete?: () => void;

  /* reveal-mode (LP concierge / wg). */
  bubbles?: RevealBubble[];
  daysep?: string;

  /* explainer-mode (Onboarding „Loslegen") – ALLE Werte sind Caller-Props. */
  /** Vorbefülltes WhatsApp-Verifikations-Token (`LV-` + 32 hex). */
  waId?: string;
  /** Lokalisierte Locari-Willkommensnachricht (`waReply`, bereits `{name}`-aufgelöst). */
  waReplyText?: ReactNode;
  /** Lokalisierte Meta-Business-Notice (`metaGreeting`). */
  metaGreeting?: ReactNode;
  /** „Heute"-Separator im Chat (lokalisiert). */
  explainerDaySeparator?: string;
  /** Startet die Explainer-Schleife erst, wenn der Step betreten wird
   *  (`true`). Bei `false` steht der Chat statisch (compose). Neustart bei
   *  jedem Wiedereintritt (Toggle). Default `true`. */
  explainerActive?: boolean;
  /** Phasen-Timings (ms) – Defaults `WA_EXPLAINER_TIMINGS`; NIE hartkodiert. */
  explainerTimings?: WaExplainerTimings;

  /* Chrome-Flags – Defaults = heutiges B/Onboarding-Verhalten. */
  /** Status-bar-Zeit. Sentinel `'live'` = echte aktuelle Uhrzeit (minütlich). */
  statusbarTime?: string;
  headerName?: string;
  headerMeta?: string;
  showBack?: boolean;
  /** @deprecated Das Design entfernt Call/Video im Header – Prop bleibt für
   *  Rückwärtskompatibilität erhalten, rendert aber nichts mehr. */
  showActions?: boolean;
  showInput?: boolean;
  inputPlaceholder?: string;
  /**
   * Kipp-Winkel des Phones in Grad (inline via CSS-Var `--wa-tilt`, die die
   * `.whatsapp-phone`-Regel liest). Default `0` (gerade). Marketing-Caller
   * übergeben `tilt={-3}` (ersetzt die frühere ambiente
   * `.marketing-scope .whatsapp-phone { rotate(-3deg) }`-Regel). Der Prop setzt
   * bewusst NUR die Variable, NICHT `transform` – so behalten die Hover- und
   * die Mobil-„gerade"-CSS-Regeln die Kontrolle über den Transform.
   */
  tilt?: number;
  wrapperClassName?: string;
  ariaHidden?: boolean;
}

/**
 * Konsolidierte WhatsApp-Phone-Komponente. Eine Chrome-Hülle (438×912-Canvas,
 * exakt nach Design-Handoff), drei Inhalts-Pfade:
 *  - `mode="steps"` (Default): ReactNode-Messages mit `data-wa-step`-Hooks,
 *    Loop+reset (Default) oder runOnce.
 *  - `mode="reveal"`: einmalige, datengetriebene Bubble-Enthüllung.
 *  - `mode="explainer"`: Onboarding-Connect-Explainer (Phasen-Loop; Copy/Timings
 *    ausschließlich Caller-Props).
 *
 * Respektiert `prefers-reduced-motion` (alles sofort sichtbar, kein Typing) und
 * startet die steps/reveal-Engines erst im Viewport (IntersectionObserver),
 * außer `autoStart`.
 */
export function WhatsappPhone({
  mode = 'steps',
  steps,
  messages,
  autoStart = false,
  runOnce = false,
  onComplete,
  bubbles,
  daysep = 'Heute',
  waId = '',
  waReplyText,
  metaGreeting,
  explainerDaySeparator = 'Heute',
  explainerActive = true,
  explainerTimings = WA_EXPLAINER_TIMINGS,
  statusbarTime = '9:41',
  headerName = 'Locari',
  headerMeta = 'online',
  showBack = true,
  // `showActions` bleibt im Contract (Rückwärtskompatibilität), wird aber NICHT
  // mehr destrukturiert/gerendert: das redesignte Header hat keine Call/Video-Icons.
  showInput = true,
  inputPlaceholder = 'Nachricht',
  tilt = 0,
  wrapperClassName,
  ariaHidden,
}: WhatsappPhoneProps) {
  const isReveal = mode === 'reveal';
  const isExplainer = mode === 'explainer';
  const messagesRef = useRef<HTMLDivElement>(null);
  // Latest-ref-Pattern: onComplete-Mutation NICHT während Render halten.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // -------- live status-bar clock (opt-in via statusbarTime='live') ---------
  const isLiveClock = statusbarTime === 'live';
  const [liveTime, setLiveTime] = useState(() =>
    isLiveClock ? computeLiveTime() : statusbarTime,
  );
  useEffect(() => {
    if (!isLiveClock) return;
    const tick = () => setLiveTime(computeLiveTime());
    // Erst-Refresh + Minutentakt in Timer-Callbacks (kein synchroner setState im
    // Effekt-Body – der Initialwert kommt aus dem useState-Initializer).
    const initial = setTimeout(tick, 0);
    const id = setInterval(tick, 30_000);
    return () => {
      clearTimeout(initial);
      clearInterval(id);
    };
  }, [isLiveClock]);
  const shownTime = isLiveClock ? liveTime : statusbarTime;

  // -------- explainer phase machine (Onboarding „Loslegen") -----------------
  const [waPhase, setWaPhase] = useState<WaExplainerPhase>('compose');
  useEffect(() => {
    if (!isExplainer) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    // Alle Phasenwechsel laufen über Timer-Callbacks (kein synchroner setState im
    // Effekt-Body). Loop startet erst beim Step-Eintritt (`explainerActive`),
    // Neustart bei jedem Wiedereintritt (Effekt-Deps), Stop beim Verlassen.
    if (!explainerActive) {
      timers.push(setTimeout(() => setWaPhase('compose'), 0));
      return () => timers.forEach(clearTimeout);
    }
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      // Static: show the full conversation (reply) without typing/loop.
      timers.push(setTimeout(() => setWaPhase('reply'), 0));
      return () => timers.forEach(clearTimeout);
    }
    const run = () => {
      setWaPhase('compose');
      timers.push(setTimeout(() => setWaPhase('sent'), explainerTimings.sent));
      timers.push(
        setTimeout(() => setWaPhase('typing'), explainerTimings.typing),
      );
      timers.push(
        setTimeout(() => setWaPhase('reply'), explainerTimings.reply),
      );
      timers.push(setTimeout(run, explainerTimings.loop));
    };
    timers.push(setTimeout(run, 0));
    return () => timers.forEach(clearTimeout);
  }, [
    isExplainer,
    explainerActive,
    explainerTimings.sent,
    explainerTimings.typing,
    explainerTimings.reply,
    explainerTimings.loop,
  ]);

  // Keep the newest message flush to the bottom as phases advance.
  useEffect(() => {
    if (!isExplainer) return;
    const root = messagesRef.current;
    if (root) root.scrollTop = root.scrollHeight;
  }, [isExplainer, waPhase]);

  // -------- steps-mode engine (Loop / runOnce) ------------------------------
  useEffect(() => {
    if (isReveal || isExplainer) return;
    const root = messagesRef.current;
    if (!root || !steps) return;
    return runStepsEngine(root, {
      steps,
      autoStart,
      runOnce,
      onComplete: () => onCompleteRef.current?.(),
    });
  }, [isReveal, isExplainer, steps, autoStart, runOnce]);

  // -------- reveal-mode engine (single reveal) ------------------------------
  useEffect(() => {
    if (!isReveal) return;
    const root = messagesRef.current;
    if (!root || !bubbles) return;
    return runRevealEngine(root, { count: bubbles.length });
  }, [isReveal, bubbles]);

  const waSent =
    waPhase === 'sent' || waPhase === 'typing' || waPhase === 'reply';
  const waComposing = waPhase === 'compose';

  // aria-hidden ownership matches the legacy markup exactly:
  //  - with a wrapper: the WRAPPER carries aria-hidden.
  //  - without a wrapper: the inner `.whatsapp-phone` carries none unless the
  //    caller explicitly opts in via `ariaHidden`.
  const phoneAriaHidden =
    wrapperClassName === undefined && ariaHidden ? true : undefined;

  const phone = (
    <div
      className="whatsapp-phone"
      aria-hidden={phoneAriaHidden}
      style={{ ['--wa-tilt']: `${tilt}deg` } as CSSProperties}
    >
      <div className="whatsapp-screen">
        {/* Dynamic Island + camera lens */}
        <span className="whatsapp-island" aria-hidden="true">
          <span className="whatsapp-island-lens" />
        </span>

        <div className="whatsapp-statusbar">
          <span className="whatsapp-statusbar-time">
            <span suppressHydrationWarning>{shownTime}</span>
            {STATUSBAR_BELL_SVG}
          </span>
          {STATUSBAR_ICONS}
        </div>

        <div className="whatsapp-header">
          {showBack ? (
            <span className="whatsapp-header-back" aria-hidden="true">
              {HEADER_BACK_SVG}
              <span className="whatsapp-header-unread">1</span>
            </span>
          ) : null}
          <span className="whatsapp-header-avatar" aria-hidden="true">
            {AVATAR_SVG}
          </span>
          <div className="whatsapp-header-meta">
            <strong>{headerName}</strong>
            <span>{headerMeta}</span>
          </div>
        </div>

        <div className="whatsapp-messages" ref={messagesRef}>
          {isExplainer ? (
            <>
              <span className="whatsapp-daysep is-visible">
                {explainerDaySeparator}
              </span>
              <div className="whatsapp-meta-notice">{metaGreeting}</div>
              {waSent ? (
                <WaMessageBubble
                  dir="out"
                  visible
                  variantClass="whatsapp-bubble--id"
                  text={waId}
                  time={shownTime}
                />
              ) : null}
              {waPhase === 'typing' ? (
                <div className="whatsapp-typing is-visible" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
              ) : null}
              {waPhase === 'reply' ? (
                <WaMessageBubble
                  dir="in"
                  visible
                  variantClass="whatsapp-bubble--reply"
                  text={waReplyText}
                  time={shownTime}
                />
              ) : null}
            </>
          ) : isReveal ? (
            <>
              <span className="whatsapp-daysep is-visible">{daysep}</span>
              {(bubbles ?? []).map((bubble, index) => (
                <Fragment key={index}>
                  {index > 0 ? (
                    <div
                      className="whatsapp-typing"
                      data-wac-typing={index}
                      aria-hidden="true"
                    >
                      <span />
                      <span />
                      <span />
                    </div>
                  ) : null}
                  <WaMessageBubble
                    stepAttribute="wac"
                    step={String(index)}
                    dir="in"
                    variantClass={bubble.status ? 'has-status' : undefined}
                    text={renderText(bubble.text)}
                    time={bubble.time}
                  >
                    {bubble.status ? (
                      <span className="whatsapp-bubble-status">
                        {STATUS_CHECK_SVG}
                        {bubble.status}
                      </span>
                    ) : null}
                  </WaMessageBubble>
                </Fragment>
              ))}
            </>
          ) : (
            messages
          )}
        </div>

        {showInput ? (
          <div className="whatsapp-input">
            <span className="whatsapp-input-plus" aria-hidden="true">
              {INPUT_PLUS_SVG}
            </span>
            <div className="whatsapp-input-field">
              {isExplainer && waComposing ? (
                <span className="whatsapp-input-id">{waId}</span>
              ) : (
                <span className="whatsapp-input-placeholder">
                  {inputPlaceholder}
                </span>
              )}
              <span className="whatsapp-input-sticker" aria-hidden="true">
                {INPUT_STICKER_SVG}
              </span>
            </div>
            {/* explainer: send while composing, cam+mic when idle;
                marketing/steps/reveal: always the send button (as today). */}
            {isExplainer && !waComposing ? (
              INPUT_CAM_MIC_SVGS
            ) : (
              <span
                className={[
                  'whatsapp-input-send',
                  isExplainer && waComposing ? 'is-pulsing' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-hidden="true"
              >
                {SEND_SVG}
              </span>
            )}
          </div>
        ) : null}

        <div className="whatsapp-home-indicator" aria-hidden="true">
          <span />
        </div>
      </div>
    </div>
  );

  if (wrapperClassName !== undefined) {
    return (
      <div className={wrapperClassName} aria-hidden="true">
        {phone}
      </div>
    );
  }

  return phone;
}
